import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import AppRoutes from './components/routing/AppRoutes';
import ErrorBoundary from './components/common/ErrorBoundary';
import PaymentSuccessModal from './components/PaymentSuccessModal';
import axios from 'axios';
import { isPrerendering } from './utils/prerender';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Main App Component
 *
 * Root component with React Router integration.
 * Handles:
 * - Global authentication state
 * - User profile management
 * - Route configuration
 * - Initial data loading
 *
 * Architecture:
 * - Uses BrowserRouter for URL-based routing
 * - Delegates route rendering to AppRoutes component
 * - Provides auth context to all child routes
 * - Manages token and user profile in localStorage
 */
function App() {
  // ============================================
  // STATE MANAGEMENT
  // ============================================

  const [token, setToken] = useState(localStorage.getItem('token'));
  const [userProfile, setUserProfile] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentTier, setPaymentTier] = useState('pro');

  // ============================================
  // EFFECTS
  // ============================================

  /**
   * Handle OAuth callbacks, email verification, and payment redirects
   * This runs once on mount to check URL params
   */
  useEffect(() => {
    // Skip during prerendering
    if (isPrerendering()) return;

    const urlParams = new URLSearchParams(window.location.search);

    // Get all possible params
    const tokenParam = urlParams.get('token');
    const userId = urlParams.get('user');
    const verify = urlParams.get('verify');
    const errorMessage = urlParams.get('message');
    const payment = urlParams.get('payment');
    const provider = urlParams.get('provider');
    const errorParam = urlParams.get('error');

    // 1. Check for email verification
    if (verify === 'true' && userId && tokenParam) {
      console.log('[App] Verifying email...');

      fetch(`${API_URL}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          token: tokenParam
        })
      })
      .then(res => res.json())
      .then(data => {
        if (data.access_token) {
          setToken(data.access_token);
          localStorage.setItem('token', data.access_token);
          alert('Email verified successfully! Welcome to ResumeAnalyzer AI!');
          // Navigate to dashboard using full navigation
          window.location.href = '/dashboard';
        } else {
          alert('Error: ' + (data.error || 'Verification failed. Please try again.'));
          window.location.href = '/login';
        }
      })
      .catch(err => {
        console.error('[App] Verification error:', err);
        alert('Verification failed. Please try again.');
        window.location.href = '/login';
      });
    }
    // 2. Check for OAuth callback (Google or LinkedIn)
    else if (tokenParam && !verify) {
      console.log('[App] OAuth callback - setting token', provider || 'google');
      setToken(tokenParam);
      localStorage.setItem('token', tokenParam);
      // Navigate to dashboard using full navigation
      window.location.href = '/dashboard';
    }
    // 3. Check for payment success/cancel
    else if (payment) {
      console.log('[App] Payment result:', payment);
      const tierPurchased = urlParams.get('tier') || 'pro';
      
      if (payment === 'success') {
        // Show success modal and refresh profile
        setPaymentTier(tierPurchased);
        setShowPaymentModal(true);
        // Profile will be refreshed via the existing useEffect when token exists
        // Navigate to dashboard using full navigation
        window.location.href = '/dashboard';
      } else if (payment === 'cancel') {
        alert('Payment cancelled. You can upgrade anytime from your dashboard.');
        window.location.href = '/dashboard';
      }
    }
    // 4. Check for error message
    else if (errorMessage) {
      console.log('[App] OAuth or other error:', errorMessage);
      alert('Error: ' + decodeURIComponent(errorMessage));
      window.location.href = '/login';
    }
    // 5. Check for error param (from LinkedIn OAuth)
    else if (errorParam) {
      console.log('[App] OAuth error:', errorParam);
      let errorText = 'Authentication failed. Please try again.';
      if (errorParam === 'linkedin_auth_failed') {
        errorText = 'LinkedIn authentication failed. Please try again or use email login.';
      } else if (errorParam === 'invalid_state') {
        errorText = 'Security validation failed. Please try again.';
      }
      alert(errorText);
      window.location.href = '/login';
    }
  }, []); // Empty dependency array - runs once on mount

  // ============================================
  // AUTH HANDLERS
  // ============================================

  /**
   * Handle logout
   */
  const handleLogout = useCallback(() => {
    console.log('[App] Logging out');
    setToken(null);
    setUserProfile(null);
    localStorage.removeItem('token');
    localStorage.removeItem('userProfile');
  }, []);

  // ============================================
  // API FUNCTIONS
  // ============================================

  /**
   * Fetch user profile from API
   */
  const fetchUserProfile = useCallback(async () => {
    try {
      console.log('[App] Fetching user profile...');
      const response = await axios.get(`${API_URL}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('[App] User profile fetched:', response.data);
      setUserProfile(response.data);
      localStorage.setItem('userProfile', JSON.stringify(response.data));
    } catch (error) {
      console.error('[App] Error fetching profile:', error);

      // If unauthorized, clear token and profile
      if (error.response?.status === 401) {
        console.log('[App] Unauthorized, clearing auth state');
        handleLogout();
      }
    }
  }, [token, handleLogout]);

  /**
   * Fetch user profile on mount if token exists
   */
  useEffect(() => {
    // Skip during prerendering
    if (isPrerendering()) return;

    if (token) {
      fetchUserProfile();
    }
  }, [token, fetchUserProfile]);

  /**
   * Handle successful login
   * @param {string} newToken - JWT token
   * @param {Object} profile - User profile data
   */
  const handleLogin = (newToken, profile) => {
    console.log('[App] Login successful');
    setToken(newToken);
    setUserProfile(profile);
    localStorage.setItem('token', newToken);
    localStorage.setItem('userProfile', JSON.stringify(profile));
  };

  /**
   * Handle payment success modal close
   * Refreshes user profile to ensure credits are up to date
   */
  const handlePaymentModalClose = useCallback(() => {
    setShowPaymentModal(false);
    // Force refresh profile to get updated credits
    if (token) {
      fetchUserProfile();
    }
  }, [token, fetchUserProfile]);

  // ============================================
  // RENDER
  // ============================================

  return (
    <ErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <AppRoutes
          userProfile={userProfile}
          token={token}
          handleLogin={handleLogin}
          handleLogout={handleLogout}
          setToken={setToken}
        />

        {/* Payment Success Modal */}
        <PaymentSuccessModal
          isOpen={showPaymentModal}
          onClose={handlePaymentModalClose}
          tier={paymentTier}
          userCredits={userProfile?.credits}
        />

        {/* Vercel Analytics - Skip during prerendering */}
        {!isPrerendering() && <Analytics />}
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
